import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { amazonOAuthApi } from '../../api/endpoints';
import { AmazonOAuthAccount } from '../../types';
import { FiCheck, FiAlertCircle, FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import CountryFlag from '../../components/common/CountryFlag';

const AmazonAuthorizationPage: React.FC = () => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<AmazonOAuthAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [authorizingId, setAuthorizingId] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await amazonOAuthApi.listVendors();
      setAccounts(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('amazonAuth.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleAuthorize = useCallback(async (account: AmazonOAuthAccount) => {
    setAuthorizingId(account.id);
    try {
      const response = await amazonOAuthApi.start(account.id);
      const { authorizationUrl } = response.data;

      // Open consent flow in new window — user signs in to Vendor Central, clicks Authorize,
      // then Amazon redirects to /callback (which is handled by AmazonOAuthCallbackPage).
      // The state we generated is now cached on the backend for 15 min.
      const popup = window.open(authorizationUrl, '_blank', 'width=900,height=700');
      if (!popup) {
        toast.error(t('amazonAuth.popupBlocked'));
      }

      // Poll for refresh of accounts every 3s — when the user completes authorization,
      // the row will become Authorized.
      const interval = setInterval(async () => {
        try {
          const r = await amazonOAuthApi.listVendors();
          const updated: AmazonOAuthAccount | undefined = r.data.find((a: AmazonOAuthAccount) => a.id === account.id);
          if (updated && updated.isAuthorized && (!account.authorizedAt || updated.authorizedAt !== account.authorizedAt)) {
            setAccounts(r.data);
            toast.success(t('amazonAuth.authSuccess', { name: account.accountName }));
            setAuthorizingId(null);
            clearInterval(interval);
          }
        } catch { /* ignore polling errors */ }
      }, 3000);

      // Stop polling after 5 min
      setTimeout(() => {
        clearInterval(interval);
        if (authorizingId === account.id) setAuthorizingId(null);
      }, 5 * 60_000);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('amazonAuth.startError'));
      setAuthorizingId(null);
    }
  }, [authorizingId, t]);

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pl-PL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('amazonAuth.title')}</h1>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={fetchAccounts} disabled={loading}>
            <FiRefreshCw className={loading ? 'spin' : ''} />
            {t('common.refresh')}
          </button>
        </div>
      </div>

      <div style={{
        padding: '14px 16px', backgroundColor: 'rgba(59, 130, 246, 0.08)',
        border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px',
        marginBottom: '20px', fontSize: '13px', color: 'var(--text)',
      }}>
        <strong>{t('amazonAuth.howItWorksTitle')}</strong>
        <ol style={{ marginTop: '8px', paddingLeft: '18px', lineHeight: '1.7' }}>
          <li>{t('amazonAuth.step1')}</li>
          <li>{t('amazonAuth.step2')}</li>
          <li>{t('amazonAuth.step3')}</li>
          <li>{t('amazonAuth.step4')}</li>
        </ol>
      </div>

      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>{t('amazonAuth.country')}</th>
              <th>{t('amazonAuth.accountName')}</th>
              <th>{t('amazonAuth.marketplaceId')}</th>
              <th>{t('amazonAuth.status')}</th>
              <th>{t('amazonAuth.authorizedAt')}</th>
              <th>{t('amazonAuth.lastSync')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '24px' }}>{t('common.loading')}</td></tr>
            ) : accounts.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                {t('amazonAuth.noAccounts')}
              </td></tr>
            ) : accounts.map(account => (
              <tr key={account.id}>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <CountryFlag countryCode={account.country} size={20} />
                    <span style={{ fontWeight: 600 }}>{account.country || '—'}</span>
                  </span>
                </td>
                <td>{account.accountName}</td>
                <td>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: 'var(--text-muted)' }}>
                    {account.marketplaceId || '—'}
                  </span>
                </td>
                <td>
                  {account.isAuthorized ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                      fontWeight: 500, color: '#10b981',
                      backgroundColor: 'rgba(16, 185, 129, 0.12)',
                    }}>
                      <FiCheck /> {t('amazonAuth.authorized')}
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                      fontWeight: 500, color: '#ef4444',
                      backgroundColor: 'rgba(239, 68, 68, 0.12)',
                    }}>
                      <FiAlertCircle /> {t('amazonAuth.notAuthorized')}
                    </span>
                  )}
                </td>
                <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {formatDateTime(account.authorizedAt)}
                </td>
                <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {formatDateTime(account.lastSyncAt)}
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleAuthorize(account)}
                    disabled={authorizingId === account.id}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <FiExternalLink />
                    {authorizingId === account.id
                      ? t('amazonAuth.waiting')
                      : account.isAuthorized
                        ? t('amazonAuth.reauthorize')
                        : t('amazonAuth.authorize')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AmazonAuthorizationPage;
